import React from "react";
import styled from "styled-components";
import { insider } from "routes/paths";
import { Container } from "components/generic/layout";
import { Button } from "components/generic/kit";
import { Overlay } from "components/common/insider";
import { NarrowBody } from "components/generic/layout";
import {
  Title,
  Row,
  Label,
  ErrorBox,
  Error
} from "components/generic/form/service";
import {
  Input,
  Formik,
  PhotoUpload,
  Select,
  Checkbox,
  CustomTimePicker,
  Autocomplete
} from "components/generic/form/formik";

import { Timezone } from "core/utils";
import { Days } from "core/utils/constants";

const stripeUrl = `https://connect.stripe.com/express/oauth/authorize?redirect_uri=${
  process.env.REACT_APP_SITE_URL
}${insider.insiderPaymentCallback}&client_id=${
  process.env.REACT_APP_STRIPE_CLIENT_ID
}`;

export default ({
  paymentInfo,
  accountBalance,
  positionsInfo,
  countries,
  availabilities,
  companies,
  fetchCompanies,
  roles,
  fetchRoles,
  profileValidationSchema,
  positionValidationSchema,
  availabilityValidationSchema,
  profileInitialValues,
  positionInitialValue,
  availabilityInitialValue,
  profileErrors,
  positionsErrors,
  availabilitiesErrors,
  editApi,
  addPositionsApi,
  addAvailabilityApi,
  updateAvatarApi,
  onSubmitAvatar,
  onSubmitProfile,
  onSubmitPositions,
  onSubmitAvailability,
  onDeletePositionClick,
  onDeleteAvailabilityClick
}) => {
  return (
    <Overlay>
      <Container>
        <NarrowBody>
          <Formik
            initialValues={profileInitialValues}
            onSubmit={onSubmitAvatar}
          >
            {props => (
              <form onSubmit={props.handleSubmit}>
                <Row>
                  <PhotoUpload
                    type="file"
                    name="photo"
                    photo={profileInitialValues.avatar}
                  />
                </Row>
                {props.dirty && (
                  <div className="tc mb2">
                    <Button
                      size="small"
                      type="submit"
                      disabled={updateAvatarApi.isFetching}
                    >
                      Update avatar
                    </Button>
                  </div>
                )}
              </form>
            )}
          </Formik>
          {paymentInfo.data && (
            <div className="mb4 mt4">
              <Title>Money earned by {profileInitialValues.first_name} </Title>
              <div>
                <div>
                  Total earned: <b>${paymentInfo.data.earned_balance}</b>
                </div>
                {!!paymentInfo.data.external_accounts && (
                  <React.Fragment>
                    <div>
                      Account Type:
                      <b>
                        {paymentInfo.data.external_accounts[0].account_type}
                      </b>
                    </div>
                    <div>
                      Account Number:
                      <b>
                        ************{
                          paymentInfo.data.external_accounts[0].last_4_digits
                        }
                      </b>
                    </div>
                  </React.Fragment>
                )}
                {!!paymentInfo.data.expected_balance && (
                  <div>
                    Upcoming earnings:{" "}
                    <b>${paymentInfo.data.expected_balance}</b>
                  </div>
                )}
              </div>
            </div>
          )}
          <Formik
            initialValues={profileInitialValues}
            validationSchema={profileValidationSchema}
            errors={profileErrors.field}
            onSubmit={onSubmitProfile}
          >
            {props => (
              <div className="pb5">
                <Title>Personal Information</Title>
                {!!profileErrors.nonField.length && (
                  <ErrorBox errors={profileErrors.nonField} />
                )}
                <form onSubmit={props.handleSubmit}>
                  <Row>
                    <Label title="First name">
                      <Input
                        name="first_name"
                        type="text"
                        placeholder="Enter your first name"
                      />
                    </Label>
                  </Row>
                  <Row>
                    <Label title="Last name">
                      <Input
                        name="last_name"
                        type="text"
                        placeholder="Enter your last name"
                      />
                    </Label>
                  </Row>

                  <Row>
                    <Label title="Phone">
                      <Input
                        name="phone"
                        type="text"
                        placeholder="Enter your phone number"
                      />
                    </Label>
                  </Row>
                  <Row>
                    <Label title="Timezone">
                      <Select name="timezone" type="select">
                        {Timezone.map((i, index) => (
                          <option key={index} value={i}>
                            {i}
                          </option>
                        ))}
                      </Select>
                    </Label>
                  </Row>
                  <Row>
                    <Label title="Notifications">
                      <Checkbox
                        name="send_sms_notifications"
                        title="Send SMS notifications"
                      />
                    </Label>
                  </Row>
                  <Row />
                  <div className="mt3 flex items-center">
                    <Button
                      size="small"
                      type="submit"
                      color="white"
                      disabled={editApi.isFetching}
                    >
                      Submit changes
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </Formik>

          <div className="pb5">
            <Title>Jobs positions</Title>

            <div className="f6">
              <React.Fragment>
                {positionsInfo.data && positionsInfo.data.length > 0 ? (
                  positionsInfo.data.map((item, index) => (
                    <Item key={index}>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Company</div>
                        </div>
                        <div className="flex w-50">
                          <div className="w-70">{item.company.name}</div>
                          <div className="w-30"> </div>
                          <DeleteItem
                            onClick={() => onDeletePositionClick(item.id)}
                            className="red f7 pointer"
                          >
                            Remove
                          </DeleteItem>
                        </div>
                      </ItemRow>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Role</div>
                        </div>
                        <div className="w-50">{item.title_name}</div>
                      </ItemRow>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Country</div>
                        </div>
                        <div className="w-50">{item.country}</div>
                      </ItemRow>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">City</div>
                        </div>
                        <div className="w-50">{item.city}</div>
                      </ItemRow>
                    </Item>
                  ))
                ) : (
                  <div>
                    <h4>You have no positions, create the first one below:</h4>
                    {!!positionsErrors.nonField.length && (
                      <ErrorBox errors={positionsErrors.nonField} />
                    )}
                  </div>
                )}
              </React.Fragment>
              <Formik
                validationSchema={positionValidationSchema}
                onSubmit={onSubmitPositions}
                initialValues={positionInitialValue}
              >
                {props => (
                  <div className="mt3">
                    <form onSubmit={props.handleSubmit}>
                      <Row>
                        <Label title="Company">
                          <Autocomplete
                            id="company"
                            name="company_name"
                            onType={fetchCompanies}
                            inputProps={{
                              placeholder: "Enter company name"
                            }}
                            items={companies}
                            getItemValue={item => item.title}
                            renderItem={(item, isHighlighted) => (
                              <div
                                key={item.id}
                                style={{
                                  background: isHighlighted
                                    ? "lightgray"
                                    : "white"
                                }}
                              >
                                {item.title}
                              </div>
                            )}
                          />
                        </Label>
                      </Row>
                      <Row>
                        <Label title="Role">
                          <Autocomplete
                            id="role"
                            name="title_name"
                            onType={fetchRoles}
                            inputProps={{
                              placeholder: "Enter your role"
                            }}
                            items={roles}
                            getItemValue={item => item.title}
                            renderItem={(item, isHighlighted) => (
                              <div
                                key={item.id}
                                style={{
                                  background: isHighlighted
                                    ? "lightgray"
                                    : "white"
                                }}
                              >
                                {item.title}
                              </div>
                            )}
                          />
                        </Label>
                      </Row>
                      <Row>
                        <Label title="Country">
                          <Select name="country">
                            {countries.data &&
                              countries.data.length > 0 &&
                              countries.data.map((item, i) => (
                                <option key={i} value={item.name}>
                                  {item.name}
                                </option>
                              ))}
                          </Select>
                        </Label>
                      </Row>
                      <Row>
                        <Label title="City">
                          <Input
                            type="text"
                            placeholder="Enter company city"
                            name="city"
                          />
                        </Label>
                      </Row>
                      <div className="mt3 flex items-center">
                        <Button
                          type="submit"
                          size="small"
                          disabled={addPositionsApi.isFetching}
                        >
                          Add Position
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </Formik>
            </div>
          </div>
          <div className="pb5">
            <Title>Availability</Title>

            <div className="f6">
              <React.Fragment>
                {availabilities.data && availabilities.data.length > 0 ? (
                  availabilities.data.map((item, index) => (
                    <Item key={index}>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Day</div>
                        </div>
                        <div className="flex w-50">
                          <div className="w-70">{item.day}</div>
                          <div className="w-30"> </div>
                          <DeleteItem
                            onClick={() => onDeleteAvailabilityClick(item.id)}
                            className="red f7 pointer"
                          >
                            Remove
                          </DeleteItem>
                        </div>
                      </ItemRow>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Timezone</div>
                        </div>
                        <div className="w-50">{item.time_zone}</div>
                      </ItemRow>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Time Range Beginning</div>
                        </div>
                        <div className="w-50">{item.time_start}</div>
                      </ItemRow>
                      <ItemRow className="flex">
                        <div className="w-50">
                          <div className="fw6">Time Ranging Ending</div>
                        </div>
                        <div className="w-50">{item.time_end}</div>
                      </ItemRow>
                    </Item>
                  ))
                ) : (
                  <div>
                    <h4>
                      You have no availabilities, create the first one below:
                    </h4>
                    {!!availabilitiesErrors.nonField.length && (
                      <ErrorBox errors={availabilitiesErrors.nonField} />
                    )}
                  </div>
                )}
              </React.Fragment>
              <Formik
                validationSchema={availabilityValidationSchema}
                onSubmit={onSubmitAvailability}
                initialValues={availabilityInitialValue}
              >
                {props => (
                  <div className="mt3">
                    <form onSubmit={props.handleSubmit}>
                      <Row>
                        <Label title="Availability Day">
                          <Select name="day" type="select">
                            <option key="" value="" />
                            {Days.map((i, index) => (
                              <option key={index} value={i.value}>
                                {i.name}
                              </option>
                            ))}
                          </Select>
                        </Label>
                        {props.errors.day &&
                          props.touched.day && (
                            <Error>{props.errors.day}</Error>
                          )}
                      </Row>
                      <Row>
                        <Label title="Timezone">
                          <Select name="time_zone" type="select">
                            <option key="" value="" />
                            {Timezone.map((i, index) => (
                              <option key={index} value={i}>
                                {i}
                              </option>
                            ))}
                          </Select>
                        </Label>
                        {props.errors.time_zone &&
                          props.touched.time_zone && (
                            <Error>{props.errors.time_zone}</Error>
                          )}
                      </Row>
                      <Row>
                        <Label title="Time Start">
                          <CustomTimePicker name="time_start" />
                        </Label>
                        {props.errors.time_start &&
                          props.touched.time_start && (
                            <Error>{props.errors.time_start}</Error>
                          )}
                      </Row>
                      <Row>
                        <Label title="Time End">
                          <CustomTimePicker name="time_end" />
                        </Label>
                        {props.errors.time_end &&
                          props.touched.time_end && (
                            <Error>{props.errors.time_end}</Error>
                          )}
                      </Row>
                      {addAvailabilityApi.error &&
                        addAvailabilityApi.error.message && (
                          <Error> {addAvailabilityApi.error.message} </Error>
                        )}
                      <div className="mt3 flex items-center">
                        <Button
                          type="submit"
                          size="small"
                          disabled={addAvailabilityApi.isFetching}
                        >
                          Add Availability
                        </Button>
                      </div>
                    </form>
                  </div>
                )}
              </Formik>
            </div>
          </div>

          <div className="pb5">
            <Title>Payment Info</Title>

            <div className="bg-white pb3 br2 f6 overflow-auto">
              {paymentInfo.error && (
                <a href={stripeUrl}>
                  <Button size="small">Enter payment details</Button>
                </a>
              )}
              {accountBalance &&
                accountBalance.data && (
                  <a target="_blank" href={accountBalance.data.url}>
                    <Button size="small">Edit Payment Account</Button>
                  </a>
                )}
            </div>
          </div>
        </NarrowBody>
      </Container>
    </Overlay>
  );
};

const DeleteItem = styled.div`
  display: none;
`;

const Item = styled.div`
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dcdcdc;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
  &:hover {
    ${DeleteItem} {
      display: block;
    }
  }
`;

const ItemRow = styled.div`
  margin-bottom: 0.325rem;
  &:last-child {
    margin-bottom: 0;
  }
`;



// WEBPACK FOOTER //
// ./src/components/pages/Insider/Profile/View.js